import { useEffect, useState, type CSSProperties } from "react";
import { useLocation } from "react-router";

import { Column } from "@/components";
import { DashboardStatRowAllView } from "@/components/dashboard/dashboard-stats/DashboardStatRowAllView";
import { DashboardStatRowIndividualView } from "@/components/dashboard/dashboard-stats/DashboardStatRowIndividualView";

import { ALL_USERS_KEY, screenTimeTier } from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { dashboardPrototype } from "@/lib/dashboardPrototype";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  DashboardSelectedUser,
  GoalPeriod,
  PerformanceTier,
} from "@/types";

export const DashboardStatRow = ({
  goalsPeriod,
  kpiOverview,
  // Stays mounted while the header minimizes so the grid track can animate
  // closed; cards fade out and clip inside the shrinking row.
  isMinimized,
  isPending,
  onGoalsPeriodChange,
  selectedUserId,
}: {
  goalsPeriod: GoalPeriod;
  kpiOverview: DashboardKpiOverview | undefined;
  isMinimized: boolean;
  isPending: boolean;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  selectedUserId: DashboardSelectedUser;
}) => {
  /* React Hooks */
  const [comparisonMetric, setComparisonMetric] =
    useState<DashboardComparisonMetric>(
      DashboardComparisonMetric.TIME_TO_SCREEN,
    );
  const location = useLocation();

  // Every staffer switch and every fresh navigation reopens on time to screen.
  useEffect(() => {
    setComparisonMetric(DashboardComparisonMetric.TIME_TO_SCREEN);
  }, [location.key, selectedUserId]);

  /* Helpers */
  const itemClassName = cn(
    "min-h-0 min-w-0 transition-opacity duration-300 ease-in-out",
    isMinimized
      ? "pointer-events-none overflow-hidden opacity-0"
      : "overflow-visible",
  );

  const selectedCardColor = () => {
    if (comparisonMetric === DashboardComparisonMetric.SUBMITS)
      return "rgb(var(--lever-surface-action))";
    if (comparisonMetric === DashboardComparisonMetric.REVENUE)
      return "rgb(var(--lever-surface-accent-green))";
    let overview = kpiOverview;
    if (import.meta.env.DEV) {
      overview =
        selectedUserId === ALL_USERS_KEY
          ? dashboardPrototype.overview(goalsPeriod)
          : dashboardPrototype.individualOverview(goalsPeriod, selectedUserId);
    }
    const hours = dashboardKpis.periodFor(overview, goalsPeriod)?.totals
      .medianHoursToFirstScreen;
    const tier = hours == null ? PerformanceTier.RED : screenTimeTier(hours);
    if (tier === PerformanceTier.GREEN)
      return "rgb(var(--lever-surface-accent-green))";
    if (tier === PerformanceTier.ORANGE)
      return "rgb(var(--lever-surface-accent-copper))";
    return "rgb(var(--lever-icon-error))";
  };

  /* Render */
  const render = () => {
    if (selectedUserId === ALL_USERS_KEY) {
      return (
        <DashboardStatRowAllView
          comparisonMetric={comparisonMetric}
          goalsPeriod={goalsPeriod}
          isPending={import.meta.env.DEV ? false : isPending}
          itemClassName={itemClassName}
          onGoalsPeriodChange={onGoalsPeriodChange}
          onMetricChange={setComparisonMetric}
          overview={
            import.meta.env.DEV
              ? dashboardPrototype.overview(goalsPeriod)
              : kpiOverview
          }
        />
      );
    }

    return (
      <DashboardStatRowIndividualView
        comparisonMetric={comparisonMetric}
        goalsPeriod={goalsPeriod}
        isPending={import.meta.env.DEV ? false : isPending}
        itemClassName={itemClassName}
        onGoalsPeriodChange={onGoalsPeriodChange}
        onMetricChange={setComparisonMetric}
        overview={
          import.meta.env.DEV
            ? dashboardPrototype.individualOverview(goalsPeriod, selectedUserId)
            : kpiOverview
        }
        selectedUserId={selectedUserId}
      />
    );
  };

  return (
    <Column
      className="contents"
      style={
        {
          "--lever-analysis-accent": selectedCardColor(),
          "--lever-analysis-outline":
            comparisonMetric === DashboardComparisonMetric.TIME_TO_SCREEN
              ? "rgb(var(--lever-border-light))"
              : selectedCardColor(),
          "--lever-kpi-selected-outline":
            comparisonMetric === DashboardComparisonMetric.TIME_TO_SCREEN
              ? "rgb(var(--lever-border-action))"
              : selectedCardColor(),
        } as CSSProperties
      }
    >
      {render()}
    </Column>
  );
};
